// Copyright 2025-2026 Florian Gallon. All Rights reserved.
import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

export default function Faq({ isFr }) {
  const [open, setOpen] = useState(null);

  const items = isFr
    ? [
        {
          q: 'Pour qui est Digi-Bakery ?',
          a: 'Pour les micro-boulangeries artisanales (1 à 2 personnes) qui produisent 10 à 50 pains par semaine et vendent sur les marchés, à domicile ou en petite boutique. Si vous gérez aujourd’hui votre boulangerie avec des carnets et des tableurs, Digi-Bakery est fait pour vous.',
        },
        {
          q: 'Comment le calcul des recettes se fait-il automatiquement ?',
          a: 'Vous créez vos recettes une fois (par exemple : 1 baguette = 250 g de farine, 150 g d’eau…). Ensuite, pour 20 commandes de baguettes, Digi-Bakery calcule qu’il vous faut 5 kg de farine, 3 L d’eau, etc. Plus besoin de calculatrice.',
        },
        {
          q: 'Est-ce que j’obtiens une boutique en ligne ?',
          a: 'Oui. Chaque compte inclut une boutique en ligne à votrenom.digi-bakery.com. Les clients parcourent les produits et commandent 24h/24. Le paiement Stripe intégré est inclus si vous souhaitez encaisser en ligne ; vous pouvez aussi n’utiliser la boutique que pour les commandes en personne. Elle est comprise dans les 20 $/mois.',
        },
        {
          q: 'Ça fonctionne si je vends sur plusieurs marchés ?',
          a: 'Oui, c’est précisément l’usage prévu. Vous définissez tous vos points de vente (marché du mardi, marché du samedi, boutique partenaire…) et Digi-Bakery vous aide à planifier ce qu’il faut cuire pour chaque jour.',
        },
        {
          q: 'Est-ce compliqué si je ne suis pas très à l’aise avec l’informatique ?',
          a: 'Non. Si vous savez envoyer un e-mail, vous saurez utiliser Digi-Bakery. L’interface est simple et pensée pour des boulangers artisans.',
        },
        {
          q: 'Que se passe-t-il après l’essai gratuit de 30 jours ?',
          a: 'Après 30 jours, vous êtes facturé 20 $/mois (ou 20 €/mois, 15 £/mois, 25 $ CAD selon votre région). Vous pouvez annuler à tout moment avant la fin de l’essai ; aucune carte bancaire n’est demandée pour démarrer.',
        },
        {
          q: 'Mes données sont-elles sécurisées ?',
          a: 'Oui. Vos données (recettes, commandes, stock) sont chiffrées et stockées sur Firebase (Google Cloud), avec des sauvegardes automatiques. Nous ne vendons jamais vos données.',
        },
        {
          q: 'Comment Digi-Bakery aide-t-il pour la sécurité alimentaire et la conformité ?',
          a: 'Digi-Bakery enregistre automatiquement chaque ingrédient, chaque fournée et chaque vente, avec une traçabilité complète. Vous savez toujours ce que vous avez cuit, quand, et avec quels numéros de lot. Tout est enregistré et exportable, ce qui simplifie les audits.',
        },
        {
          q: 'Puis-je annuler mon abonnement à tout moment ?',
          a: 'Oui. Pas de contrat, pas de frais d’annulation. Vous arrêtez quand vous voulez depuis les paramètres de votre compte.',
        },
      ]
    : [
        {
          q: 'Who is Digi-Bakery for?',
          a: 'Artisan micro-bakeries — one- or two-person operations baking 10 to 50 loaves a week and selling at markets, from home, or in a small shop. If you run your bakery on notebooks and spreadsheets today, Digi-Bakery is for you.',
        },
        {
          q: 'How does it calculate my recipes automatically?',
          a: 'You create your recipes once (for example: 1 baguette = 250g flour, 150g water…). Then, for 20 baguette orders, Digi-Bakery works out that you need 5kg of flour, 3L of water, and so on. No calculator needed.',
        },
        {
          q: 'Do I get an online shop?',
          a: 'Yes. Every account includes an online shop at yourname.digi-bakery.com. Customers browse products and order around the clock. Integrated Stripe payment is included if you want to take payment online, or you can use the shop for in-person orders only. It is part of the $20/month.',
        },
        {
          q: 'Does it work if I sell at several different markets?',
          a: 'Yes — that is exactly what it is for. Define all your selling points (Tuesday market, Saturday market, partner shop…) and Digi-Bakery helps you plan what to bake for each day.',
        },
        {
          q: 'Is it complicated if I am not very tech-savvy?',
          a: 'No. If you can send an email, you can use Digi-Bakery. The interface is simple and designed for artisan bakers.',
        },
        {
          q: 'What happens after the 30-day free trial?',
          a: 'After 30 days you are charged $20/month (or €20/month, £15/month, $25 CAD depending on your region). Cancel anytime before the trial ends; no credit card is required to start.',
        },
        {
          q: 'Is my data secure?',
          a: 'Yes. Your data (recipes, orders, stock) is encrypted and stored on Firebase (Google Cloud), with automatic backups. We never sell your data.',
        },
        {
          q: 'How does Digi-Bakery help with food safety and compliance?',
          a: 'Digi-Bakery records every ingredient, batch and sale automatically, with complete traceability. You always know what you baked, when, and with which lot numbers. Everything is recorded and exportable, which makes audits straightforward.',
        },
        {
          q: 'Can I cancel my subscription anytime?',
          a: 'Yes. No contract, no cancellation fees. Stop whenever you want from your account settings.',
        },
      ];

  return (
    <section id="faq" className="dbk-section dbk-section--alt dbk-section--line">
      <div className="dbk-wrap">
        <div className="dbk-head">
          <h2 className="dbk-h2">{isFr ? 'Questions fréquentes' : 'Frequently asked questions'}</h2>
        </div>

        <div className="dbk-faq">
          {items.map((item, i) => (
            <div className="dbk-faq__item" data-open={open === i} key={item.q}>
              <button
                type="button"
                className="dbk-faq__q"
                aria-expanded={open === i}
                aria-controls={`dbk-faq-a-${i}`}
                onClick={() => setOpen(open === i ? null : i)}
              >
                {item.q}
                <ChevronDown size={20} strokeWidth={2} />
              </button>
              <div className="dbk-faq__awrap">
                <div className="dbk-faq__a" id={`dbk-faq-a-${i}`} role="region">
                  <p>{item.a}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
